import {
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { isApiError, normalizeError, type ApiError } from '../lib/api/errors';

type UseApiQueryOptions<TQueryFnData, TData = TQueryFnData> = Omit<
  UseQueryOptions<TQueryFnData, ApiError, TData>,
  'queryFn'
> & {
  queryFn: (ctx: QueryFunctionContext) => Promise<TQueryFnData>;
  /** Set to false to suppress the automatic error toast */
  showErrorToast?: boolean;
};

/**
 * Wraps useQuery with ApiError typing and automatic error toast via Sonner.
 * The global QueryCache onError handler fires the toast unless suppressed.
 * Pass showErrorToast={false} to suppress the toast for a specific query.
 *
 * The QueryFunctionContext (including AbortSignal) is forwarded to queryFn so
 * callers can pass signal to Axios, enabling request cancellation on unmount.
 */
export function useApiQuery<TQueryFnData, TData = TQueryFnData>(
  options: UseApiQueryOptions<TQueryFnData, TData>,
): UseQueryResult<TData, ApiError> {
  const { showErrorToast = true, queryFn, ...rest } = options;

  return useQuery<TQueryFnData, ApiError, TData>({
    ...rest,
    meta: { ...options.meta, suppressToast: showErrorToast === false },
    queryFn: async (ctx) => {
      try {
        return await queryFn(ctx);
      } catch (err) {
        throw isApiError(err) ? err : normalizeError(err);
      }
    },
  });
}
